// OS background classifier (DESIGN_SPEC §12.4). Background is hidden by default and always
// counted, with a per-service breakdown. A network event is background when:
//   DNS   its name is a Windows background name, or it is a CDN name (akamai, fastly, …) that
//         answers a query which was itself background (never on its own: attacker-usable);
//   flow / conn  the port is LLMNR, mDNS, SSDP, NetBIOS, DHCP(v6) or WS-Discovery, the address
//         is non-routable (RFC 1918, loopback, link-local, multicast, broadcast, ULA), or the
//         address was answered only for background names;
//   http  the host is a background name.
// CDN answers and the flows to their addresses count toward the service of the query they answer
// (a39.d.akamai.net for www.msftconnecttest.com is NCSI), so the breakdown names what Windows did.
//   classifyNoise(events) → NoiseIndex   one pass over a seq-ordered event list (memoize on snap.version)
//   createNoiseTracker() → { update(events) → NoiseIndex }   the same result, folding only the events appended since
//                          the last update (the live store's list grows in place); an insert before the end rebuilds
//   serviceOfName · isCdnName · addressClass · splitHostPort · backgroundIoc · breakdown
import type { Event } from "@/types";
import type { NoiseHit, NoiseIndex, NoiseReason, NoiseService } from "./types";

const NAME_SERVICES: readonly (readonly [string, NoiseService])[] = [
  ["msftconnecttest.com", "NCSI"],
  ["msftncsi.com", "NCSI"],
  ["windowsupdate.com", "Windows Update"],
  ["update.microsoft.com", "Windows Update"],
  ["mp.microsoft.com", "Windows Update"], // delivery.mp / do.dsp.mp: Delivery Optimization
  ["microsoft.map.fastly.net", "Windows Update"], // Microsoft's own Fastly map, not the shared CDN
  ["sls.microsoft.com", "Microsoft"],
  ["login.live.com", "Microsoft"],
  ["g.live.com", "Microsoft"],
  ["config.edge.skype.com", "Microsoft"],
  ["msedge.api.cdp.microsoft.com", "Microsoft"],
  ["fs.microsoft.com", "Microsoft"],
  ["settings-win.data.microsoft.com", "Microsoft"],
  ["events.data.microsoft.com", "Microsoft"],
  ["time.windows.com", "Microsoft"],
  ["ocsp.digicert.com", "Certificates"],
  ["crl.microsoft.com", "Certificates"],
  ["bing.com", "Bing"],
  ["bing.net", "Bing"],
];

// CDN owner names. Microsoft's edge names carry a short hyphenated prefix (ln-msedge.net, tm-azurefd.net).
const CDN_NAMES: readonly RegExp[] = [
  /(^|\.)akamai\.net$/,
  /(^|\.)akamaiedge\.net$/,
  /(^|\.)akadns\.net$/,
  /(^|\.)edgekey\.net$/,
  /(^|\.)edgesuite\.net$/,
  /(^|\.)trafficmanager\.net$/,
  /(^|\.)fastly\.net$/,
  /(^|\.)([a-z0-9]{1,4}-)?azurefd\.net$/,
  /(^|\.)azureedge\.net$/,
  /(^|\.)([a-z0-9]{1,4}-)?msedge\.net$/,
];

const PORT_SERVICES: ReadonlyMap<number, NoiseService> = new Map([
  [5355, "LLMNR"],
  [5353, "mDNS"],
  [1900, "SSDP"],
  [137, "NetBIOS"],
  [138, "NetBIOS"],
  [67, "DHCP"],
  [68, "DHCP"],
  [546, "DHCP"],
  [547, "DHCP"],
  [3702, "WS-Discovery"],
]);

/** A DNS answer links to the query it answers when it lands within this window. */
const ANSWER_WINDOW_MS = 2000;

const norm = (name: string) => name.trim().toLowerCase().replace(/\.$/, "");
const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const num = (v: unknown): number | undefined => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : undefined;
};

export function serviceOfName(name: string): NoiseService | null {
  const n = norm(name);
  if (!n) return null;
  for (const [suffix, svc] of NAME_SERVICES) if (n === suffix || n.endsWith(`.${suffix}`)) return svc;
  return null;
}


export function isCdnName(name: string): boolean {
  const n = norm(name);
  return !!n && CDN_NAMES.some((re) => re.test(n));
}

/** "192.0.2.10:443" · "[fe80::1]:443" · "ff02::1:2" (bare IPv6, no port) · "host:8080". */
export function splitHostPort(s: string): { host: string; port?: number } {
  const v = s.trim();
  const b = /^\[([^\]]+)\](?::(\d+))?$/.exec(v);
  if (b) return { host: b[1], port: b[2] ? Number(b[2]) : undefined };
  const colons = v.split(":").length - 1;
  if (colons === 1) {
    const [h, p] = v.split(":");
    return { host: h, port: /^\d+$/.test(p) ? Number(p) : undefined };
  }
  return { host: v };
}

/** Non-routable classes: "local" (RFC 1918, loopback, link-local, unspecified, broadcast, ULA) or "multicast". */
export function addressClass(ip: string): "local" | "multicast" | null {
  const a = ip.trim().toLowerCase();
  if (!a) return null;
  if (a.includes(":")) {
    if (a === "::1" || a === "::") return "local";
    const head = parseInt(a.split(":")[0] || "0", 16);
    if (a.startsWith("ff")) return "multicast";
    if ((head & 0xffc0) === 0xfe80) return "local"; // fe80::/10
    if ((head & 0xfe00) === 0xfc00) return "local"; // fc00::/7 unique local
    return null;
  }
  const o = a.split(".").map(Number);
  if (o.length !== 4 || o.some((x) => !Number.isInteger(x) || x < 0 || x > 255)) return null;
  const [p, q] = o;
  if (p >= 224 && p <= 239) return "multicast";
  if (p === 10 || p === 127 || p === 0) return "local";
  if (p === 172 && q >= 16 && q <= 31) return "local";
  if (p === 192 && q === 168) return "local";
  if (p === 169 && q === 254) return "local";
  if (a === "255.255.255.255") return "local";
  return null;
}


const ADDRESS_SERVICE = { local: "Local network", multicast: "Multicast" } as const;

function classifyAddress(
  dst: string,
  dport: number | undefined,
  sport: number | undefined,
  bgIps: ReadonlyMap<string, NoiseService>,
): NoiseHit | null {
  const { host, port } = splitHostPort(dst);
  const p = dport ?? port;
  const byPort = p !== undefined ? PORT_SERVICES.get(p) : undefined;
  if (byPort) return { service: byPort, reason: "port" };
  const cls = addressClass(host);
  if (cls) {
    // Replies to LLMNR/mDNS/NetBIOS go to an ephemeral port on the guest: name them by source port.
    const bySource = sport !== undefined ? PORT_SERVICES.get(sport) : undefined;
    return bySource ? { service: bySource, reason: "port" } : { service: ADDRESS_SERVICE[cls], reason: "address" };
  }
  const svc = bgIps.get(host);
  return svc ? { service: svc, reason: "answer" } : null;
}

const NETWORK_KINDS = new Set(["net.dns", "net.flow", "net.conn", "net.http"]);
const REASONS: readonly NoiseReason[] = ["name", "cdn", "port", "address", "answer"];

export function classifyNoise(events: readonly Event[]): NoiseIndex {
  const hits = new Map<number, NoiseHit>();
  const bgNames = new Map<string, NoiseService>();
  const ipNames = new Map<string, string[]>();
  const bgCandidates = new Map<string, NoiseService>();
  const ipsWithSignal = new Set<string>();

  // Pass 1, DNS: names, and the CDN answers of background queries.
  let lastQuery: { svc: NoiseService | null; at: number } | null = null;
  for (const e of events) {
    if (e.kind !== "net.dns") continue;
    const f = e.fields;
    const at = Date.parse(e.ts);
    const qname = norm(str(f.qname));
    if (qname) {
      const svc = serviceOfName(qname);
      if (svc) {
        hits.set(e.seq, { service: svc, reason: "name" });
        bgNames.set(qname, svc);
      }
      lastQuery = { svc, at };
      continue;
    }
    const name = norm(str(f.name));
    const ip = str(f.ip).trim();
    let hit: NoiseHit | null = null;
    const svc = name ? serviceOfName(name) : null;
    if (svc) hit = { service: svc, reason: "name" };
    else if (name && isCdnName(name) && lastQuery?.svc && Math.abs(at - lastQuery.at) <= ANSWER_WINDOW_MS) {
      hit = { service: lastQuery.svc, reason: "cdn" };
    }
    if (hit) {
      hits.set(e.seq, hit);
      if (name) bgNames.set(name, hit.service);
    }
    if (ip) {
      const names = ipNames.get(ip) ?? [];
      if (name && !names.includes(name)) names.push(name);
      ipNames.set(ip, names);
      if (hit) {
        if (!bgCandidates.has(ip)) bgCandidates.set(ip, hit.service);
      } else ipsWithSignal.add(ip);
    }
  }
  // An address also answered for a non-background name is ambiguous: keep it visible.
  const bgIps = new Map([...bgCandidates].filter(([ip]) => !ipsWithSignal.has(ip)));

  // Pass 2: flows, connections, HTTP.
  let network = 0;
  for (const e of events) {
    if (!NETWORK_KINDS.has(e.kind)) continue;
    network++;
    if (e.kind === "net.dns") continue;
    const f = e.fields;
    let hit: NoiseHit | null = null;
    if (e.kind === "net.http") {
      const { host } = splitHostPort(str(f.host));
      const svc = serviceOfName(host);
      // A literal address as the host follows the address rules (no port rule for HTTP).
      hit = svc ? { service: svc, reason: "name" } : classifyAddress(host, undefined, undefined, bgIps);
    } else {
      hit = classifyAddress(str(f.dst), num(f.dport), num(f.sport), bgIps);
    }
    if (hit) hits.set(e.seq, hit);
  }

  const serviceCounts = new Map<NoiseService, number>();
  const byReason = Object.fromEntries(REASONS.map((r) => [r, 0])) as Record<NoiseReason, number>;
  for (const h of hits.values()) {
    serviceCounts.set(h.service, (serviceCounts.get(h.service) ?? 0) + 1);
    byReason[h.reason]++;
  }
  const byService = [...serviceCounts].sort((a, b) => b[1] - a[1]);

  return {
    hits,
    total: hits.size,
    network,
    byService,
    byReason,
    bgNames,
    bgIps,
    ipNames,
    isBackground: (e) => hits.has(typeof e === "number" ? e : e.seq),
  };
}

/** classifyNoise, incrementally. DNS is sequential (an answer looks only at earlier queries), so each new event is
 *  classified once; a flow that only an answered address can explain is kept per address and re-read whenever that
 *  address enters or leaves the background set (a later answer can do either). */
export function createNoiseTracker() {
  let list: readonly Event[] | null = null;
  let folded = 0;
  let tailSeq = -1;
  let hits = new Map<number, NoiseHit>();
  let bgNames = new Map<string, NoiseService>();
  let ipNames = new Map<string, string[]>();
  let bgCandidates = new Map<string, NoiseService>();
  let ipsWithSignal = new Set<string>();
  let bgIps = new Map<string, NoiseService>();
  let pending = new Map<string, number[]>(); // address → seqs of flows/conns/http decided only by it
  let lastQuery: { svc: NoiseService | null; at: number } | null = null;
  let network = 0;
  let services = new Map<NoiseService, number>();
  let reasons = Object.fromEntries(REASONS.map((r) => [r, 0])) as Record<NoiseReason, number>;
  let index: NoiseIndex | null = null;

  function reset() {
    folded = 0;
    tailSeq = -1;
    hits = new Map();
    bgNames = new Map();
    ipNames = new Map();
    bgCandidates = new Map();
    ipsWithSignal = new Set();
    bgIps = new Map();
    pending = new Map();
    lastQuery = null;
    network = 0;
    services = new Map();
    reasons = Object.fromEntries(REASONS.map((r) => [r, 0])) as Record<NoiseReason, number>;
  }

  function setHit(seq: number, hit: NoiseHit | null) {
    const old = hits.get(seq);
    if (old) {
      services.set(old.service, (services.get(old.service) ?? 1) - 1);
      reasons[old.reason]--;
      hits.delete(seq);
    }
    if (hit) {
      hits.set(seq, hit);
      services.set(hit.service, (services.get(hit.service) ?? 0) + 1);
      reasons[hit.reason]++;
    }
  }

  /** Recompute one address's membership in bgIps; re-read the flows that hang on it when it changes. */
  function settle(ip: string) {
    const svc = !ipsWithSignal.has(ip) ? bgCandidates.get(ip) : undefined;
    if (svc === bgIps.get(ip)) return;
    if (svc) bgIps.set(ip, svc);
    else bgIps.delete(ip);
    for (const s of pending.get(ip) ?? []) setHit(s, svc ? { service: svc, reason: "answer" } : null);
  }

  function dns(e: Event) {
    const f = e.fields;
    const at = Date.parse(e.ts);
    const qname = norm(str(f.qname));
    if (qname) {
      const svc = serviceOfName(qname);
      if (svc) {
        setHit(e.seq, { service: svc, reason: "name" });
        bgNames.set(qname, svc);
      }
      lastQuery = { svc, at };
      return;
    }
    const name = norm(str(f.name));
    const ip = str(f.ip).trim();
    let hit: NoiseHit | null = null;
    const svc = name ? serviceOfName(name) : null;
    if (svc) hit = { service: svc, reason: "name" };
    else if (name && isCdnName(name) && lastQuery?.svc && Math.abs(at - lastQuery.at) <= ANSWER_WINDOW_MS) hit = { service: lastQuery.svc, reason: "cdn" };
    if (hit) {
      setHit(e.seq, hit);
      if (name) bgNames.set(name, hit.service);
    }
    if (ip) {
      const names = ipNames.get(ip) ?? [];
      if (name && !names.includes(name)) names.push(name);
      ipNames.set(ip, names);
      if (hit) {
        if (!bgCandidates.has(ip)) bgCandidates.set(ip, hit.service);
      } else ipsWithSignal.add(ip);
      settle(ip);
    }
  }

  function flow(e: Event) {
    const f = e.fields;
    let host: string;
    let fixed: NoiseHit | null = null;
    if (e.kind === "net.http") {
      host = splitHostPort(str(f.host)).host;
      const svc = serviceOfName(host);
      fixed = svc ? { service: svc, reason: "name" } : classifyAddress(host, undefined, undefined, EMPTY_IPS);
    } else {
      host = splitHostPort(str(f.dst)).host;
      fixed = classifyAddress(str(f.dst), num(f.dport), num(f.sport), EMPTY_IPS);
    }
    if (fixed) {
      setHit(e.seq, fixed);
      return;
    }
    const list = pending.get(host);
    if (list) list.push(e.seq);
    else pending.set(host, [e.seq]);
    const svc = bgIps.get(host);
    if (svc) setHit(e.seq, { service: svc, reason: "answer" });
  }

  function snapshot(): NoiseIndex {
    const byService = [...services].filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
    const h = hits;
    return {
      hits: h,
      total: h.size,
      network,
      byService,
      byReason: { ...reasons },
      bgNames,
      bgIps,
      ipNames,
      isBackground: (e) => h.has(typeof e === "number" ? e : e.seq),
    };
  }

  return {
    update(events: readonly Event[]): NoiseIndex {
      const appended = list === events && events.length >= folded && (folded === 0 || events[folded - 1]?.seq === tailSeq);
      if (!appended) {
        reset();
        list = events;
      } else if (index && events.length === folded) return index;
      for (let i = folded; i < events.length; i++) {
        const e = events[i];
        if (!NETWORK_KINDS.has(e.kind)) continue;
        network++;
        if (e.kind === "net.dns") dns(e);
        else flow(e);
      }
      folded = events.length;
      tailSeq = folded ? events[folded - 1].seq : -1;
      index = snapshot();
      return index;
    },
  };
}

const EMPTY_IPS: ReadonlyMap<string, NoiseService> = new Map();

/** Is an indicator OS background? Uses the run's index for CDN names and answered addresses. */
export function backgroundIoc(kind: string, value: string, idx?: NoiseIndex): NoiseService | null {
  const v = value.trim();
  if (kind === "domain") return serviceOfName(v) ?? idx?.bgNames.get(norm(v)) ?? null;
  if (kind === "ipv4" || kind === "ipv6") {
    const cls = addressClass(v);
    return cls ? ADDRESS_SERVICE[cls] : idx?.bgIps.get(v) ?? null;
  }
  if (kind === "url") {
    let host = "";
    try {
      host = new URL(v).hostname.replace(/^\[|\]$/g, "");
    } catch {
      return null;
    }
    return backgroundIoc(/^[\d.]+$/.test(host) ? "ipv4" : host.includes(":") ? "ipv6" : "domain", host, idx);
  }
  return null;
}

/** "NCSI 76 · LLMNR 29 · mDNS 29 · NetBIOS 15 · SSDP 13 · …" */
export function breakdown(idx: Pick<NoiseIndex, "byService">, max = 5): string {
  const shown = idx.byService.slice(0, max).map(([s, n]) => `${s} ${n}`);
  return idx.byService.length > max ? `${shown.join(" · ")} · …` : shown.join(" · ");
}
